import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import './index.css';

// Complete Wordle word list loaded from local file 'wordle_answers.txt' and split by new lines
// Ensure you have this file in the 'src' directory with one 5-letter word per line
import wordListText from '../wordle_answers.txt';

type Color = '' | 'gray' | 'yellow' | 'green';
type Feedback = [Color, Color, Color, Color, Color];
type Guess = { word: string; feedback: Feedback };

const WORD_LIST: string[] = wordListText
  .split('\n')
  .map((word) => word.trim().toLowerCase())
  .filter((w) => w.length === 5 && /^[a-z]{5}$/.test(w));

const WordleSolver = () => {
  const [guesses, setGuesses] = useState<Guess[]>([
    { word: '', feedback: ['', '', '', '', ''] },
    { word: '', feedback: ['', '', '', '', ''] },
    { word: '', feedback: ['', '', '', '', ''] },
  ]);

  // Helper: fill the next empty round with a word
  const useWordInNextRound = (word: string) => {
    setGuesses((prev) => {
      const next = [...prev];
      const idx = next.findIndex((g) => !g.word);
      const target = idx === -1 ? 0 : idx; // if all filled, overwrite first
      next[target] = { word, feedback: ['', '', '', '', ''] };
      return next;
    });
  };

  const filtered = useMemo(() => {
    let possible = [...WORD_LIST];

    guesses.forEach((guess) => {
      const word = (guess.word || '').toLowerCase();
      if (word.length !== 5) return;
      const feedback = guess.feedback;

      // Build duplicate-aware constraints
      const counts: Record<string, { green: number; yellow: number; gray: number }> = {};
      for (let i = 0; i < 5; i++) {
        const ch = word[i]!;
        counts[ch] = counts[ch] || { green: 0, yellow: 0, gray: 0 };
        if (feedback[i] === 'green') counts[ch].green++;
        else if (feedback[i] === 'yellow') counts[ch].yellow++;
        else if (feedback[i] === 'gray') counts[ch].gray++;
      }
      const minCounts: Record<string, number> = {};
      const absentLetters = new Set<string>();
      for (const [ch, c] of Object.entries(counts)) {
        const min = c.green + c.yellow;
        if (min > 0) minCounts[ch] = min;
        if (min === 0 && c.gray > 0) absentLetters.add(ch);
      }

      possible = possible.filter((candidate) => {
        // Greens
        for (let i = 0; i < 5; i++) {
          if (feedback[i] === 'green' && candidate[i] !== word[i]) return false;
        }
        // Yellows
        for (let i = 0; i < 5; i++) {
          if (feedback[i] === 'yellow') {
            if (candidate[i] === word[i]) return false;
            const ch = word[i]!;
            if (!candidate.includes(ch)) return false;
          }
        }
        // Absent letters from pure grays
        for (const ch of absentLetters) {
          if (candidate.includes(ch)) return false;
        }
        // Minimum counts for letters that had any green/yellow
        if (Object.keys(minCounts).length) {
          const candCounts: Record<string, number> = {};
          for (const ch of candidate) candCounts[ch] = (candCounts[ch] ?? 0) + 1;
          for (const [ch, min] of Object.entries(minCounts)) {
            if ((candCounts[ch] ?? 0) < min) return false;
          }
        }
        return true;
      });
    });

    return possible;
  }, [guesses]);

  // Determine which round is next (0-based). If all filled, default to 0.
  const nextRoundIndex = useMemo(() => {
    const idx = guesses.findIndex(g => !g.word);
    return idx === -1 ? 0 : idx;
  }, [guesses]);

  const updateGuess = (round: number, field: 'word', value: string) => {
    setGuesses((prev) => {
      const next = [...prev];
      const g = next[round];
      if (!g) return prev;
      const sanitized = value.replace(/[^a-zA-Z]/g, '').slice(0, 5).toLowerCase();
      next[round] = { ...g, [field]: sanitized } as Guess;
      return next;
    });
  };

  const updateFeedback = (round: number, position: number) => {
    const colors: Color[] = ['', 'gray', 'yellow', 'green'];
    setGuesses((prev) => {
      const next = [...prev];
      const g = next[round];
      if (!g) return prev;
      const fb = [...g.feedback] as Feedback;
      const current = fb[position] ?? '';
      const currentIndex = colors.indexOf(current);
      const nextIndex = (currentIndex + 1) % colors.length;
      fb[position] = colors[nextIndex] ?? '';
      next[round] = { ...g, feedback: fb };
      return next;
    });
  };

  const getLetterColor = (color: Color) => {
    switch(color) {
      case 'green': return 'bg-green-500';
      case 'yellow': return 'bg-yellow-500';
      case 'gray': return 'bg-gray-400';
      default: return 'bg-gray-200';
    }
  };
  
  // // Helpers for entropy-based ranking
  // const patternCode = (guess: string, secret: string): number => {
  //   // 0 = gray, 1 = yellow, 2 = green; code in base-3 across positions
  //   const res: number[] = [0, 0, 0, 0, 0];
  //   const counts: Record<string, number> = {};
  //   for (let i = 0; i < 5; i++) {
  //     const ch = secret.charAt(i);
  //     counts[ch] = (counts[ch] ?? 0) + 1;
  //   }
  //   // Greens first
  //   for (let i = 0; i < 5; i++) {
  //     if (guess.charAt(i) === secret.charAt(i)) {
  //       res[i] = 2;
  //       const ch = guess.charAt(i);
  //       counts[ch] = (counts[ch] ?? 0) - 1;
  //     }
  //   }
  //   // Then yellows
  //   for (let i = 0; i < 5; i++) {
  //     if (res[i] === 0) {
  //       const ch = guess.charAt(i);
  //       if ((counts[ch] ?? 0) > 0) {
  //         res[i] = 1;
  //         counts[ch] = (counts[ch] ?? 0) - 1;
  //       }
  //     }
  //   }
  //   // Encode base-3
  //   let code = 0;
  //   let mul = 1;
  //   for (let i = 0; i < 5; i++) {
  //     const v = res[i] ?? 0;
  //     code += v * mul;
  //     mul *= 3;
  //   }
  //   return code;
  // };

  // const entropyForGuess = (guess: string, secrets: string[]): number => {
  //   const buckets = new Map<number, number>();
  //   for (const s of secrets) {
  //     const code = patternCode(guess, s);
  //     buckets.set(code, (buckets.get(code) ?? 0) + 1);
  //   }
  //   const n = secrets.length;
  //   let entropy = 0;
  //   for (const count of buckets.values()) {
  //     const p = count / n;
  //     entropy += -p * Math.log2(p);
  //   }
  //   return entropy;
  // };

  // const sampleDeterministic = (arr: string[], max: number): string[] => {
  //   if (arr.length <= max) return arr;
  //   const stride = Math.max(1, Math.floor(arr.length / max));
  //   const result: string[] = [];
  //   for (let i = 0; i < arr.length && result.length < max; i += stride) {
  //     const val = arr[i];
  //     if (val !== undefined) result.push(val);
  //   }
  //   return result;
  // };

  // // Ranking: expected information gain (entropy) over remaining candidates
  // const ranked = useMemo(() => {
  //   const N = filtered.length;
  //   if (N === 0) return [] as string[];

  //   // For large sets, sample secrets to keep things snappy
  //   const SAMPLE_MAX = 800;
  //   const secrets = sampleDeterministic(filtered, SAMPLE_MAX);

  //   // Optional: frequency tiebreaker to stabilize ordering
  //   const freq: Record<string, number> = {};
  //   for (const w of filtered) {
  //     const uniq = new Set(w);
  //     for (const ch of uniq) freq[ch] = (freq[ch] ?? 0) + 1;
  //   }
  //   const freqScore = (w: string) => {
  //     const seen = new Set<string>();
  //     let s = 0;
  //     for (const ch of w) {
  //       if (!seen.has(ch)) s += freq[ch] ?? 0;
  //       else s -= 0.25;
  //       seen.add(ch);
  //     }
  //     return s;
  //   };

  //   const scored = filtered.map((g) => ({ g, e: entropyForGuess(g, secrets) }));
  //   scored.sort((a, b) => {
  //     if (b.e !== a.e) return b.e - a.e;
  //     // tiebreak by frequency score
  //     return freqScore(b.g) - freqScore(a.g);
  //   });
  //   return scored.map((s) => s.g);
  // }, [filtered]);

  // Optimized Wordle Solver with Information Theory
interface ScoredWord {
  word: string;
  entropy: number;
  expected: number;
}

// Generate pattern code (0=gray, 1=yellow, 2=green) encoded as base-3 integer
const patternCode = (guess: string, secret: string): number => {
  const result = new Array(5).fill(0);
  const secretChars = secret.split('');
  const guessChars = guess.split('');
  
  // Mark greens first (exact matches)
  for (let i = 0; i < 5; i++) {
    const gc = guessChars[i];
    const sc = secretChars[i];
    if (gc !== undefined && sc !== undefined && gc === sc) {
      result[i] = 2;
      secretChars[i] = '!'; // Mark as used (! won't match any letter)
      guessChars[i] = '?';   // Different marker for guess
    }
  }
  
  // Mark yellows (right letter, wrong position)
  for (let i = 0; i < 5; i++) {
    const gc = guessChars[i];
    if (gc !== undefined && gc !== '?') {
      const idx = secretChars.indexOf(gc);
      if (idx !== -1) {
        result[i] = 1;
        secretChars[idx] = '!'; // Mark as used
      }
    }
  }
  
  // Convert to base-3 integer (more efficient than string comparison)
  let code = 0;
  for (let i = 0; i < 5; i++) {
    code = code * 3 + (result[i] ?? 0);
  }
  return code;
};

// Calculate entropy (information gain) for a guess
const calculateEntropy = (guess: string, possibleSecrets: string[]): number => {
  const patternCounts = new Map<number, number>();
  
  // Count how many secrets produce each pattern
  for (const secret of possibleSecrets) {
    const pattern = patternCode(guess, secret);
    patternCounts.set(pattern, (patternCounts.get(pattern) ?? 0) + 1);
  }
  
  // Calculate Shannon entropy: -Σ p(x) * log2(p(x))
  const total = possibleSecrets.length;
  let entropy = 0;
  
  for (const count of patternCounts.values()) {
    if (count > 0) {
      const p = count / total;
      entropy -= p * Math.log2(p);
    }
  }
  
  return entropy;
};

// Calculate expected remaining words after guess
const calculateExpectedRemaining = (guess: string, possibleSecrets: string[]): number => {
  const patternCounts = new Map<number, number>();
  
  for (const secret of possibleSecrets) {
    const pattern = patternCode(guess, secret);
    patternCounts.set(pattern, (patternCounts.get(pattern) ?? 0) + 1);
  }
  
  const total = possibleSecrets.length;
  let expected = 0;
  
  for (const count of patternCounts.values()) {
    const p = count / total;
    expected += p * count; // Probability × remaining words in that bucket
  }
  
  return expected;
};

// Smart sampling for performance with large word sets
const smartSample = (words: string[], maxSize: number, seed?: number): string[] => {
  if (words.length <= maxSize) return words;
  
  const result: string[] = [];
  const seen = new Set<number>();
  
  // Deterministic sampling if seed provided (for consistency)
  const getRandom = seed !== undefined
    ? () => {
        seed = (seed! * 9301 + 49297) % 233280;
        return seed / 233280;
      }
    : Math.random;
  
  // Always include first and last (often common/uncommon words)
  result.push(words[0]!);
  if (words.length > 1) result.push(words[words.length - 1]!);
  seen.add(0);
  seen.add(words.length - 1);
  
  // Stratified sampling: divide into buckets and sample from each
  const bucketSize = Math.ceil(words.length / (maxSize - 2));
  
  for (let i = 0; i < words.length && result.length < maxSize; i += bucketSize) {
    const idx = i + Math.floor(getRandom() * Math.min(bucketSize, words.length - i));
    if (!seen.has(idx) && words[idx]) {
      result.push(words[idx]);
      seen.add(idx);
    }
  }
  
  return result;
};

// Main ranking function
const rankWords = (
  filtered: string[],
  allValidWords?: string[],
  options: {
    metric?: 'entropy' | 'expected' | 'hybrid';
    maxSecrets?: number;
    maxCandidates?: number;
    includeAllWords?: boolean;
    hardMode?: boolean;
  } = {}
): string[] => {
  const {
    metric = 'entropy',
    maxSecrets = 1000,
    maxCandidates = 2000,
    includeAllWords = true,
    hardMode = false
  } = options;

  const N = filtered.length;
  
  // Edge cases
  if (N === 0) return [];
  if (N === 1) return filtered;
  if (N === 2) return filtered; // Just guess one when only 2 left
  
  // Determine candidate words to evaluate
  let candidates: string[];
  
  if (hardMode || !allValidWords || !includeAllWords) {
    // Hard mode: only use filtered words
    candidates = filtered;
  } else if (N > 20) {
    // Early game: consider all valid words for better information gain
    // But limit to reasonable number for performance
    candidates = smartSample(allValidWords, maxCandidates, 42);
    // Always include all possible answers in our candidate set
    const filteredSet = new Set(filtered);
    for (const word of filtered) {
      if (!candidates.includes(word)) {
        candidates.push(word);
      }
    }
  } else {
    // Endgame: focus on possible answers
    candidates = filtered;
  }
  
  // Sample secrets for performance if needed
  const secrets = N > maxSecrets ? smartSample(filtered, maxSecrets, 42) : filtered;
  
  // Calculate scores for each candidate
  const scored: ScoredWord[] = candidates.map(word => {
    const entropy = metric !== 'expected' ? calculateEntropy(word, secrets) : 0;
    const expected = metric !== 'entropy' ? calculateExpectedRemaining(word, secrets) : Infinity;
    
    return { word, entropy, expected };
  });
  
  // Sort by chosen metric
  scored.sort((a, b) => {
    if (metric === 'entropy') {
      // Higher entropy is better
      if (b.entropy !== a.entropy) return b.entropy - a.entropy;
      // Tiebreak: prefer words that are possible answers
      const aIsPossible = filtered.includes(a.word);
      const bIsPossible = filtered.includes(b.word);
      if (aIsPossible !== bIsPossible) return aIsPossible ? -1 : 1;
      // Final tiebreak: lower expected remaining
      return a.expected - b.expected;
    } else if (metric === 'expected') {
      // Lower expected remaining is better
      if (a.expected !== b.expected) return a.expected - b.expected;
      // Tiebreak: prefer words that are possible answers
      const aIsPossible = filtered.includes(a.word);
      const bIsPossible = filtered.includes(b.word);
      if (aIsPossible !== bIsPossible) return aIsPossible ? -1 : 1;
      // Final tiebreak: higher entropy
      return b.entropy - a.entropy;
    } else {
      // Hybrid: combine both metrics
      const aScore = a.entropy / Math.log2(1 + a.expected);
      const bScore = b.entropy / Math.log2(1 + b.expected);
      if (bScore !== aScore) return bScore - aScore;
      // Tiebreak with possibility
      const aIsPossible = filtered.includes(a.word);
      const bIsPossible = filtered.includes(b.word);
      return aIsPossible !== bIsPossible ? (aIsPossible ? -1 : 1) : 0;
    }
  });
  
  return scored.map(s => s.word);
};

// Exploration-focused ranking for the second guess
// Ignores constraints from the first word and prioritizes discovering new letters and positions.
const rankExploration = (
  usedLetters: Set<string>,
  options: {
    maxSecrets?: number;
    maxCandidates?: number;
  } = {}
): string[] => {
  const { maxSecrets = 1000, maxCandidates = 2000 } = options;

  // Secrets are the full answer list to maximize general information gain
  const secrets = WORD_LIST.length > maxSecrets ? smartSample(WORD_LIST, maxSecrets, 42) : WORD_LIST;

  // Prefer candidates with all-unique letters and no overlap with already used letters
  const isUnique = (w: string) => new Set(w).size === 5;
  const overlapCount = (w: string) => {
    let c = 0;
    for (const ch of w) if (usedLetters.has(ch)) c++;
    return c;
  };

  // Primary candidate set: unique letters and zero overlap
  let candidates = WORD_LIST.filter((w) => isUnique(w) && overlapCount(w) === 0);

  // If too few, widen gradually
  if (candidates.length < 50) {
    const widen1 = WORD_LIST.filter((w) => isUnique(w) && overlapCount(w) <= 1);
    // Merge while preserving order and removing duplicates
    const seen = new Set(candidates);
    for (const w of widen1) if (!seen.has(w)) { candidates.push(w); seen.add(w); }
  }
  if (candidates.length < 10) {
    const widen2 = WORD_LIST.filter((w) => overlapCount(w) === 0);
    const seen = new Set(candidates);
    for (const w of widen2) if (!seen.has(w)) { candidates.push(w); seen.add(w); }
  }

  // Keep computation reasonable on very large sets
  if (candidates.length > maxCandidates) {
    candidates = smartSample(candidates, maxCandidates, 7);
  }

  // Score by entropy against the broad secret set
  const scored = candidates.map((word) => ({
    word,
    entropy: calculateEntropy(word, secrets),
    overlap: overlapCount(word),
    unique: isUnique(word) ? 1 : 0,
  }));

  // Sort: fewer overlaps first, unique letters first, then higher entropy
  scored.sort((a, b) => {
    if (a.overlap !== b.overlap) return a.overlap - b.overlap; // prefer 0 overlap
    if (b.unique !== a.unique) return b.unique - a.unique;     // prefer unique
    return b.entropy - a.entropy;                               // then info gain
  });

  return scored.map(s => s.word);
};

// React useMemo wrapper
const ranked = useMemo(() => {
  // On the second turn, switch to exploration: ignore first-round constraints
  if (nextRoundIndex === 1) {
    const firstWord = (guesses[0]?.word || '').toLowerCase();
    const used = new Set<string>();
    for (const ch of firstWord) used.add(ch);
    return rankExploration(used, { maxSecrets: 1000, maxCandidates: 2000 });
  }

  // Rounds 1 and 3+: use constrained ranking (current behavior)
  return rankWords(filtered, undefined, {
    metric: 'entropy',        // or 'expected' or 'hybrid'
    maxSecrets: 1000,         // Sample secrets if filtered.length > this
    maxCandidates: 2000,      // Max candidate words to evaluate
    includeAllWords: true,    // Consider non-answer words as guesses
    hardMode: false           // Set true to only guess from filtered list
  });
}, [filtered, guesses, nextRoundIndex]);

  const suggestions = ranked.slice(0, 100);

  const bestStarters = ['crane', 'slate', 'trace', 'crate', 'slant', 'stare'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <h1 className="text-4xl font-bold text-center mb-2 text-indigo-600">
            Wordle Solver
          </h1>
          <p className="text-center text-gray-600 mb-8">
            3 rounds to solve the puzzle
          </p>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-green-800 mb-3 flex items-center gap-2">
              <span className="text-2xl">🎯</span>
              Best Opening Words
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {bestStarters.map((word, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    setGuesses((prev) => {
                      const next = [...prev];
                      next[0] = { word, feedback: ['', '', '', '', ''] };
                      return next;
                    });
                  }}
                  className="bg-white px-4 py-3 rounded-lg text-center font-mono font-bold text-green-700 uppercase border-2 border-green-400 shadow-sm hover:bg-green-50 hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-300 cursor-pointer transition-colors"
                  title="Click to use this opening word"
                >
                  {word}
                </button>
              ))}
            </div>
            <p className="text-sm text-green-700 mt-3">
              These words use the most common letters in optimal positions. <strong>CRANE</strong> and <strong>SLATE</strong> are top choices!
            </p>
          </div>

          <div className="space-y-6">
            {guesses.map((guess, roundIdx) => (
              <div key={roundIdx} className="border-2 border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-4 mb-3">
                  <span className="font-bold text-lg text-gray-700">
                    Round {roundIdx + 1}
                  </span>
                </div>

                <div className="flex flex-col gap-3">
                  <input
                    type="text"
                    maxLength={5}
                    value={guess.word}
                    onChange={(e) => updateGuess(roundIdx, 'word', e.target.value.toLowerCase())}
                    placeholder="Enter your guess"
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg text-lg uppercase tracking-widest focus:outline-none focus:border-indigo-500 text-gray-900 bg-white placeholder-gray-400"
                  />

                  {/* Inline preview to ensure visibility of typed guess */}
                  <div className="flex gap-2 justify-center">
                    {[0, 1, 2, 3, 4].map((pos) => (
                      <div
                        key={`preview-${pos}`}
                        className="w-10 h-10 flex items-center justify-center rounded-md border border-gray-300 bg-gray-50 font-mono font-bold text-gray-700 text-xl uppercase"
                      >
                        {guess.word[pos] || ''}
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2 justify-center">
                    {[0, 1, 2, 3, 4].map((pos) => (
                      <button
                        key={pos}
                        onClick={() => updateFeedback(roundIdx, pos)}
                        className={`w-14 h-14 flex items-center justify-center rounded-lg font-bold text-white text-xl uppercase transition-all duration-150 ${getLetterColor((guess.feedback[pos] ?? '') as Color)}`}
                      >
                        {guess.word[pos] || ''}
                      </button>
                    ))}
                  </div>

                  <p className="text-xs text-center text-gray-500">
                    Click letters to cycle: Empty → Gray → Yellow → Green
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-indigo-50 rounded-lg p-6 min-h-[200px]">
            <div className="flex items-center gap-2 mb-4">
              <Search className="text-indigo-600" />
              <h2 className="text-xl font-bold text-indigo-900 flex items-center gap-3">
                Suggested Words <span className="text-sm font-medium text-indigo-700">({Math.min(suggestions.length, filtered.length)} shown of {filtered.length})</span>
              </h2>
            </div>
            
            {filtered.length === 0 ? (
              <p className="text-gray-600">No matching words found. Check your feedback colors!</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {suggestions.map((word, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => useWordInNextRound(word)}
                    className="bg-white rounded-lg border-2 border-indigo-200 px-3 py-2 text-center font-mono font-bold text-indigo-600 uppercase hover:bg-indigo-50 hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 cursor-pointer transition-colors"
                    title="Click to use this word"
                  >
                    {word}
                  </button>
                ))}
              </div>
            )}
            {filtered.length > suggestions.length && (
              <p className="text-sm text-gray-600 mt-3 text-center">
                + {filtered.length - suggestions.length} more words
              </p>
            )}
          </div>

          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h3 className="font-bold text-gray-700 mb-2">How to use:</h3>
            <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
              <li>Enter your guess in the input field</li>
              <li>Click each letter to set its color based on the game feedback</li>
              <li>Gray = not in word, Yellow = in word but wrong spot, Green = correct spot</li>
              <li>View suggested words below and pick your next guess</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WordleSolver;